import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from "@/components/ui/command";
import { Plus, Trash2, Save, ArrowLeft, UserPlus, FileText, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { CustomerSelect } from "@/components/CustomerSelect";

const todayISO = () => new Date().toISOString().slice(0, 10);
const blankItem = () => ({ description: "", quantity: 1, unit: "Nos", weight_per_unit: 0 });

const defaultForm = () => ({
  pl_ref: "",
  pl_date: todayISO(),
  customer_name: "",
  customer_place: "",
  customer_details: "",
  customer_contact: "",
  customer_trn: "",
  items: [blankItem()],
  gross_weight: "",
  hs_code: "",
  packing: "Loose",
  cbm: "",
  notes: "",
  source_quote_id: null,
});

const num = (n) => Number(n || 0);

const PackingListFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(defaultForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [grossManual, setGrossManual] = useState(false);  // false => Gross auto-tracks Net

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    apiClient.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiClient.get(`/packing-lists/${id}`).then(({ data }) => {
      setForm({ ...defaultForm(), ...data, items: data.items?.length ? data.items : [blankItem()] });
      // If a saved gross weight exists, treat it as a manual override so we don't clobber it.
      setGrossManual(Boolean((data.gross_weight || "").trim()));
      setLoading(false);
    }).catch(() => { toast.error("Failed to load packing list"); nav("/packing-lists"); });
  }, [id, isEdit, nav]);

  useEffect(() => {
    if (isEdit) return;
    apiClient.get("/packing-lists/next-ref").then(({ data }) => {
      setForm((f) => (f.pl_ref ? f : { ...f, pl_ref: data.pl_ref }));
    }).catch(() => {});
  }, [isEdit]);

  const applySavedCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_name: c.name || "",
      customer_place: c.place || "",
      customer_details: c.details || "",
      customer_contact: c.contact || "",
      customer_trn: c.trn || "",
    }));
    toast.success(`Loaded customer: ${c.name}`);
  };

  const saveCustomer = async () => {
    if (!form.customer_name.trim()) return toast.error("Enter a customer name first");
    setSavingCustomer(true);
    try {
      await apiClient.post("/customers", {
        name: form.customer_name,
        place: form.customer_place,
        contact: form.customer_contact,
        details: form.customer_details,
        trn: form.customer_trn,
      });
      toast.success("Customer saved to directory");
      setCustomerRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save customer");
    }
    setSavingCustomer(false);
  };

  const applyQuote = async (quote) => {
    try {
      const { data } = await apiClient.get(`/quotes/${quote.id}`);
      const items = (data.line_items || []).map((li) => ({
        description: li.description || (li.specs || []).find((s) => s.label?.toLowerCase() === "model")?.value || "",
        quantity: num(li.quantity) || 1,
        unit: "Nos",
        weight_per_unit: 0,
      }));
      setForm((f) => ({
        ...f,
        customer_name: data.customer_name || f.customer_name,
        customer_place: data.customer_place || f.customer_place,
        customer_details: data.customer_details || f.customer_details,
        customer_contact: data.customer_contact || f.customer_contact,
        items: items.length ? items : f.items,
        source_quote_id: data.id,
      }));
      toast.success(`Loaded ${items.length} item(s) from ${data.quote_ref}`);
    } catch {
      toast.error("Failed to load quote");
    }
    setQuoteOpen(false);
  };

  const setItem = (idx, key, value) =>
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...f, items };
    });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const totalQty = form.items.reduce((s, it) => s + num(it.quantity), 0);
  const totalNet = form.items.reduce((s, it) => s + num(it.quantity) * num(it.weight_per_unit), 0);
  const unitLabel = form.items.find((it) => it.unit)?.unit || "Nos";

  // Gross Weight automatically equals total Net Weight (unless manually overridden).
  useEffect(() => {
    if (grossManual) return;
    const auto = `${totalNet.toFixed(2)} Kg`;
    setForm((f) => (f.gross_weight === auto ? f : { ...f, gross_weight: auto }));
  }, [totalNet, grossManual]);

  const submit = async () => {
    if (!form.pl_ref.trim()) return toast.error("PL Number is required");
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (form.items.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/packing-lists/${id}`, form);
        toast.success("Packing list updated");
        nav(`/packing-lists/${id}`);
      } else {
        const { data } = await apiClient.post("/packing-lists", form);
        toast.success("Packing list created");
        nav(`/packing-lists/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6 pb-20" data-testid="packing-list-form-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="rounded-sm" data-testid="pl-back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold ep-text-primary">
              {isEdit ? "Edit Packing List" : "New Packing List"}
            </h1>
            <p className="text-sm text-slate-500">Fill in the details, then save to generate the PDF.</p>
          </div>
        </div>
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={submit} disabled={saving} data-testid="save-packing-list-button">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : isEdit ? "Update" : "Save"}
        </Button>
      </div>

      {/* Section 1: Details */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">1 • Packing List Details</h2>
          {!isEdit && (
            <div className="w-full sm:w-72" data-testid="pl-quote-picker">
              <Popover open={quoteOpen} onOpenChange={setQuoteOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" className="w-full justify-between rounded-sm font-normal" data-testid="pl-quote-trigger">
                    <span className="flex items-center gap-2 truncate">
                      <FileText className="h-4 w-4 shrink-0 text-[#1E40AF]" />
                      Start from a quote…
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-sm" align="end">
                  <Command>
                    <CommandInput placeholder="Search quote ref or customer…" data-testid="pl-quote-search" />
                    <CommandList>
                      <CommandEmpty>No quotes found.</CommandEmpty>
                      <CommandGroup>
                        {quotes.map((qt) => (
                          <CommandItem
                            key={qt.id}
                            value={`${qt.quote_ref} ${qt.customer_name}`}
                            onSelect={() => applyQuote(qt)}
                            data-testid={`pl-quote-option-${qt.id}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{qt.quote_ref}</span>
                              <span className="text-[11px] text-slate-500">{qt.customer_name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="PL Number *">
            <div className="flex gap-1">
              <Input value={form.pl_ref} onChange={(e) => set("pl_ref", e.target.value)} placeholder="PL-002935/26" className="rounded-sm" data-testid="pl-ref-input" />
              <Button
                type="button" variant="outline" size="sm" className="rounded-sm whitespace-nowrap"
                onClick={async () => {
                  try {
                    const { data } = await apiClient.get("/packing-lists/next-ref");
                    set("pl_ref", data.pl_ref);
                    toast.success(`Next PL number: ${data.pl_ref}`);
                  } catch { toast.error("Failed to fetch next number"); }
                }}
                data-testid="pl-auto-ref-button"
              >
                Auto
              </Button>
            </div>
          </Field>
          <Field label="Date *">
            <Input type="date" value={form.pl_date} onChange={(e) => set("pl_date", e.target.value)} className="rounded-sm" data-testid="pl-date-input" />
          </Field>
        </div>
      </Card>

      {/* Section 2: Customer */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">2 • Customer (Bill To)</h2>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-72" data-testid="pl-customer-picker">
              <CustomerSelect value={form.customer_name} onSelect={applySavedCustomer} refreshKey={customerRefreshKey} />
            </div>
            <Button type="button" variant="outline" size="sm" className="rounded-sm whitespace-nowrap" onClick={saveCustomer} disabled={savingCustomer} data-testid="pl-save-customer-button">
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              {savingCustomer ? "Saving…" : "Save Customer"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Customer Name *">
            <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Company name" className="rounded-sm" data-testid="pl-customer-name-input" />
          </Field>
          <Field label="Place / City">
            <Input value={form.customer_place} onChange={(e) => set("customer_place", e.target.value)} placeholder="Jebel Ali, Dubai" className="rounded-sm" data-testid="pl-customer-place-input" />
          </Field>
          <Field label="TRN (Tax Registration No.)">
            <Input value={form.customer_trn} onChange={(e) => set("customer_trn", e.target.value)} placeholder="100xxxxxxxxxxxx" className="rounded-sm" data-testid="pl-customer-trn-input" />
          </Field>
          <Field label="Contact Number">
            <Input value={form.customer_contact} onChange={(e) => set("customer_contact", e.target.value)} placeholder="+971-50-…" className="rounded-sm" data-testid="pl-customer-contact-input" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Address / Details">
              <Textarea value={form.customer_details} onChange={(e) => set("customer_details", e.target.value)} rows={2} placeholder="Full address, free zone, country…" className="rounded-sm" data-testid="pl-customer-details-input" />
            </Field>
          </div>
        </div>
      </Card>

      {/* Section 3: Items */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">3 • Items</h2>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={addItem} data-testid="pl-add-item-button">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Item
          </Button>
        </div>
        <div className="hidden grid-cols-12 gap-2 px-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 md:grid">
          <div className="col-span-5">Item Description</div>
          <div className="col-span-2 text-right">Quantity</div>
          <div className="col-span-1">Unit</div>
          <div className="col-span-2 text-right">Weight/Unit (Kg)</div>
          <div className="col-span-1 text-right">Net (Kg)</div>
          <div className="col-span-1" />
        </div>
        <div className="space-y-2">
          {form.items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 items-center gap-2 rounded-sm border border-[#E5E7EB] p-2" data-testid={`pl-item-${idx}`}>
              <div className="col-span-12 md:col-span-5">
                <Textarea value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} rows={1} placeholder="Plastic Pallet-EPP03-Blue" className="rounded-sm min-h-9" data-testid={`pl-item-desc-${idx}`} />
              </div>
              <div className="col-span-4 md:col-span-2">
                <Input type="number" min={0} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="rounded-sm text-right tabular-nums" data-testid={`pl-item-qty-${idx}`} />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Input value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} placeholder="Nos" className="rounded-sm" data-testid={`pl-item-unit-${idx}`} />
              </div>
              <div className="col-span-3 md:col-span-2">
                <Input type="number" min={0} step="0.01" value={it.weight_per_unit} onChange={(e) => setItem(idx, "weight_per_unit", e.target.value)} className="rounded-sm text-right tabular-nums" data-testid={`pl-item-weight-${idx}`} />
              </div>
              <div className="col-span-1 flex h-9 items-center justify-end pr-1 text-sm font-semibold tabular-nums ep-text-primary" data-testid={`pl-item-net-${idx}`}>
                {(num(it.quantity) * num(it.weight_per_unit)).toFixed(2)}
              </div>
              <div className="col-span-1 flex justify-end">
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => removeItem(idx)} data-testid={`pl-remove-item-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 ml-auto flex w-full max-w-xs flex-col gap-1 rounded-sm border border-[#E5E7EB] bg-slate-50 p-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Total Quantity</span><span className="font-semibold tabular-nums" data-testid="pl-total-qty">{totalQty} {unitLabel}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Total Net Weight</span><span className="font-semibold tabular-nums" data-testid="pl-total-net">{totalNet.toFixed(2)} Kg</span></div>
        </div>
      </Card>

      {/* Section 4: Summary */}
      <Card className="rounded-sm border-[#E5E7EB] p-6 shadow-none">
        <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-wider text-slate-500">4 • Shipment Summary</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Gross Weight {!grossManual && <span className="ml-1 normal-case text-[10px] font-normal text-emerald-600">(auto = Net Weight)</span>}
              </Label>
              {grossManual && (
                <button
                  type="button"
                  className="text-[10px] font-semibold uppercase tracking-wider text-[#1E40AF] hover:underline"
                  onClick={() => { setGrossManual(false); setForm((f) => ({ ...f, gross_weight: `${totalNet.toFixed(2)} Kg` })); }}
                  data-testid="pl-gross-reset-auto"
                >
                  Reset to Net
                </button>
              )}
            </div>
            <div className="mt-1">
              <Input
                value={form.gross_weight}
                onChange={(e) => { setGrossManual(true); set("gross_weight", e.target.value); }}
                placeholder="e.g. 160 Kg"
                className="rounded-sm"
                data-testid="pl-gross-weight-input"
              />
            </div>
          </div>
          <Field label="HS Code">
            <Input value={form.hs_code} onChange={(e) => set("hs_code", e.target.value)} placeholder="39231090" className="rounded-sm" data-testid="pl-hs-code-input" />
          </Field>
          <Field label="Packing">
            <Input value={form.packing} onChange={(e) => set("packing", e.target.value)} placeholder="Loose" className="rounded-sm" data-testid="pl-packing-input" />
          </Field>
          <Field label="CBM (optional)">
            <Input value={form.cbm} onChange={(e) => set("cbm", e.target.value)} placeholder="e.g. 1.2" className="rounded-sm" data-testid="pl-cbm-input" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes (optional)">
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm" data-testid="pl-notes-input" />
            </Field>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={submit} disabled={saving} data-testid="save-packing-list-bottom">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Saving…" : isEdit ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default PackingListFormPage;
